import type { RhythmLayoutKey } from "./rhythmLayoutKeys";

export type RhythmKeyColor = {
  R: number;
  G: number;
  B: number;
  isCustom?: boolean;
};

/** 实体键盘最低可见亮度（对齐 ai-hub hardwareSafeRgb 阈值） */
export function normalizeHardwareRgb(color: {
  R: number;
  G: number;
  B: number;
}): { R: number; G: number; B: number } {
  if (color.R + color.G + color.B < 24) {
    return { R: 0, G: 0, B: 0 };
  }
  const max = Math.max(color.R, color.G, color.B);
  if (max >= 68) return color;
  const scale = 68 / Math.max(1, max);
  return {
    R: Math.min(255, Math.round(color.R * scale)),
    G: Math.min(255, Math.round(color.G * scale)),
    B: Math.min(255, Math.round(color.B * scale)),
  };
}

export type BuildHuafendaRhythmMusicDataOptions = {
  /**
   * 2.4G：SDK 按 musicData 出现顺序组包（旧版按下标、新版按序读 keyIndex 转 RGB565）。
   * 须与键盘配置遍历序一致，不可按 keyIndex 排序。
   */
  layoutOrder?: boolean;
};

/**
 * 按设备律动键位构建华奋达 setMusicData 数组。
 * 2.4G：保持 rhythmKeys 配置序；有线 USB：按 keyIndex 升序。
 */
export function buildHuafendaRhythmMusicData(
  keyColors: Record<string, RhythmKeyColor>,
  rhythmKeys: RhythmLayoutKey[],
  webCodeToLedIndex?: Readonly<Record<string, number>>,
  options?: BuildHuafendaRhythmMusicDataOptions
): number[] {
  const layoutOrder = options?.layoutOrder === true;
  const entries: Array<[number, number, number, number]> = [];

  for (const key of rhythmKeys) {
    const color = keyColors[key.code];
    if (!color && !layoutOrder) continue;
    const rgb = normalizeHardwareRgb(color ?? { R: 0, G: 0, B: 0 });
    const ledIndex = webCodeToLedIndex?.[key.code] ?? key.ledIndex;
    if (layoutOrder) {
      // 2.4G 按数组下标映射灯位，须保持与 rhythmKeys 等长，不可因 ledIndex 缺失丢格
      entries.push([
        ledIndex !== undefined && ledIndex >= 0 ? ledIndex : 0,
        rgb.R,
        rgb.G,
        rgb.B,
      ]);
      continue;
    }
    if (ledIndex === undefined || ledIndex < 0) continue;
    entries.push([ledIndex, rgb.R, rgb.G, rgb.B]);
  }

  if (!layoutOrder) {
    entries.sort((a, b) => a[0] - b[0]);
  }

  const musicData: number[] = [];
  for (const [idx, r, g, b] of entries) {
    musicData.push(idx, r, g, b);
  }
  return musicData;
}

/** 比较连续两帧 setMusicData 包体是否一致，用于 2.4G 跳过重复 HID 写入 */
export function areRhythmMusicDataEqual(a: number[], b: number[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
